import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import YouTube from 'react-youtube';
import DiscordShareModal from '../DiscordShareModal';
import { useSpotifyPlayer } from '../../contexts/SpotifyPlayerContext';
import PlayerReactions from './PlayerReactions';
import './DigginInTheCrates.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Check if mobile
const isMobileDevice = () => window.innerWidth < 768;

// Genre options for filtering - Electronic music genres only
const GENRES = [
  'All Genres', 'House', 'Techno', 'Trance', 'Drum n Bass', 'Dubstep', 
  'Ambient', 'Breakbeat', 'Hardcore', 'Jungle', 'UK Garage', 'IDM', 
  'Downtempo', 'Progressive House', 'Deep House', 'Minimal', 'Electro',
  'Disco', 'Italo Disco', 'Acid House', 'Tech House', 'Hard Techno'
];

// All electronic genres for "All Genres" filter - used to get diverse mix
const ALL_ELECTRONIC_STYLES = [
  'House', 'Techno', 'Trance', 'Drum n Bass', 'Dubstep', 
  'Ambient', 'Breakbeat', 'Hardcore', 'Jungle', 'UK Garage', 'IDM', 
  'Downtempo', 'Progressive House', 'Deep House', 'Minimal', 'Electro',
  'Disco', 'Italo Disco', 'Acid House', 'Tech House', 'Hard Techno'
];

// Country options
const COUNTRIES = [
  'Everywhere', 'US', 'UK', 'Germany', 'France', 'Netherlands', 'Belgium',
  'Italy', 'Spain', 'Japan', 'Australia', 'Canada', 'Sweden', 'Poland'
];

// Decade options
const DECADES = [
  'Since Forever', '1970s', '1980s', '1990s', '2000s', '2010s', '2020s'
];

// Rarity options
const RARITIES = [
  { id: 'all', label: 'All' },
  { id: 'common', label: 'Common' },
  { id: 'rare', label: 'Rare' },
  { id: 'gem', label: 'Gems' }
];

const DigginInTheCrates = ({ user, onAddToWishlist, onClose }) => {
  // Navigation state
  const [activeTab, setActiveTab] = useState('the-station');
  const [hotPicksSubTab, setHotPicksSubTab] = useState('most-wanted');
  
  // Spotify Player Context
  const spotifyPlayer = useSpotifyPlayer();
  const spotifyToken = localStorage.getItem('spotify_access_token');
  const isSpotifyConnected = !!spotifyToken && spotifyToken !== 'mock_token_for_testing';
  const isSpotifyReady = spotifyPlayer?.isReady && spotifyPlayer?.isPremium;
  
  // Debug Spotify status on mount
  useEffect(() => {
    console.log('🎵 Spotify Connection Status:', {
      hasToken: !!spotifyToken,
      tokenPreview: spotifyToken ? spotifyToken.substring(0, 20) + '...' : 'none',
      isConnected: isSpotifyConnected,
      playerReady: spotifyPlayer?.isReady,
      isPremium: spotifyPlayer?.isPremium,
      isSpotifyReady
    });
  }, [spotifyToken, isSpotifyConnected, spotifyPlayer, isSpotifyReady]);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGenre, setSelectedGenre] = useState('All Genres');
  const [selectedCountry, setSelectedCountry] = useState('Everywhere');
  const [selectedDecade, setSelectedDecade] = useState('Since Forever');
  const [selectedRarity, setSelectedRarity] = useState('all');
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const [isLandscape, setIsLandscape] = useState(window.innerHeight < 500 && window.innerWidth > window.innerHeight);

  // Handle resize for mobile and landscape detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsLandscape(window.innerHeight < 500 && window.innerWidth > window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Tracks state
  const [tracks, setTracks] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [nextTrack, setNextTrack] = useState(null);
  const [previousTrack, setPreviousTrack] = useState(null);
  const [trackHistory, setTrackHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  
  // Player state
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [volumeBeforeMute, setVolumeBeforeMute] = useState(0.7);
  const [currentVideoId, setCurrentVideoId] = useState(null); // YouTube fallback
  const [spotifyTrackUri, setSpotifyTrackUri] = useState(null); // Spotify URI
  const [playbackSource, setPlaybackSource] = useState(null); // 'spotify' | 'youtube' | null
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioUnavailable, setAudioUnavailable] = useState(false);
  const [showSpotifyPrompt, setShowSpotifyPrompt] = useState(false);
  
  // Social interaction state
  const [trackLikes, setTrackLikes] = useState({}); // {trackId: {liked: bool, count: num}}
  const [trackFavorites, setTrackFavorites] = useState({}); // {trackId: bool}
  const [trackReactions, setTrackReactions] = useState({}); // {trackId: {emoji: count}}
  const [showReactionPicker, setShowReactionPicker] = useState(null); // trackId or null
  const [discordShareTrack, setDiscordShareTrack] = useState(null); // Track for Discord share modal
  
  // MusicBrainz enrichment state
  const [mbTrackData, setMbTrackData] = useState(null);
  const [mbLoading, setMbLoading] = useState(false);

  // Tonearm drag state (mobile interactive needle drop)
  const [isDraggingTonearm, setIsDraggingTonearm] = useState(false);
  const [tonearmDragAngle, setTonearmDragAngle] = useState(0);
  const tonearmRef = useRef(null);
  const vinylRef = useRef(null);
  
  // Add to Crate state
  const [showAddToCrate, setShowAddToCrate] = useState(null); // Track object or null
  const [userCrates, setUserCrates] = useState([]);
  const [isLoadingCrates, setIsLoadingCrates] = useState(false);
  const [addingToCrate, setAddingToCrate] = useState(null); // crate id being added to
  
  // Reaction emojis for tracks
  const reactionEmojis = ['🔥', '❤️', '🎵', '🙌', '💃', '🎧', '💎', '🌟'];
  
  // Crate Finder state (cross-platform search)
  const [crateFinderQuery, setCrateFinderQuery] = useState('');
  const [crateFinderResults, setCrateFinderResults] = useState({
    spotify: [],
    youtube: [],
    discogs: []
  });
  const [isSearchingCrateFinder, setIsSearchingCrateFinder] = useState(false);
  const [crateFinderSearched, setCrateFinderSearched] = useState(false);
  
  // YouTube player ref (hidden - audio only fallback)
  const youtubePlayerRef = useRef(null);
  const [youtubePlayerReady, setYoutubePlayerReady] = useState(false);
  const timeIntervalRef = useRef(null);
  const shouldAutoPlayRef = useRef(false);
  
  // Ref-based cache for likes - persists across re-renders and never gets reset
  const likesCache = useRef({});
  
  // Helper to get best image URL with fallback
  const getImageUrl = (track) => {
    if (!track) return null;
    // Discogs provides thumb, cover_image, or images array
    if (track.cover_image && track.cover_image !== '') return track.cover_image;
    if (track.thumb && track.thumb !== '') return track.thumb;
    if (track.images && track.images.length > 0) {
      const primary = track.images.find(img => img.type === 'primary');
      if (primary?.uri) return primary.uri;
      if (track.images[0]?.uri) return track.images[0].uri;
    }
    return null;
  };
  
  // Handle image error - show fallback
  const handleImageError = (e) => {
    e.target.onerror = null; // Prevent infinite loop
    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150" viewBox="0 0 150 150"><rect fill="%231a1a2e" width="150" height="150"/><text x="75" y="80" font-size="40" text-anchor="middle" fill="%2300ffff">🎵</text></svg>';
  };

  // Helper to check if a track is from Discogs (not from Spotify/YouTube/Deezer/SoundCloud)
  const isDiscogsTrack = (track) => {
    if (!track) return false;
    // If track has explicit source, check it
    if (track.source) {
      return track.source === 'discogs';
    }
    // Otherwise, check for streaming platform identifiers
    if (track.spotify_uri || track.spotify_id) return false;
    if (track.youtube_id || track.video_id) return false;
    if (track.deezer_id) return false;
    if (track.soundcloud_id || track.soundcloud_url) return false;
    // If none of the above, assume it's from Discogs
    return true;
  };

  // Calculate rarity based on Discogs community data (want/have counts)
  // Note: Discogs search doesn't always return want/have, so we use heuristics
  const calculateRarity = (track) => {
    const have = track.have || 0;
    const want = track.want || 0;
    const year = parseInt(track.year) || 2000;
    const currentYear = new Date().getFullYear();
    const age = currentYear - year;
    
    // If we have actual want/have data, use it
    if (have > 0 || want > 0) {
      if (have < 50 && (want > have * 2 || have === 0)) return 'gem';
      if (have < 200) return 'rare';
      return 'common';
    }
    
    // Fallback: Use year-based heuristic for older releases
    // Older releases (20+ years) are more likely to be rare/gems
    if (age >= 30) return 'gem';
    if (age >= 20) return 'rare';
    return 'common';
  };

  // Get rarity badge style
  const getRarityBadge = (rarity) => {
    switch (rarity) {
      case 'gem':
        return { bg: 'linear-gradient(135deg, #8B5CF6, #EC4899)', label: 'Gem', icon: '💎' };
      case 'rare':
        return { bg: 'linear-gradient(135deg, #F59E0B, #EF4444)', label: 'Rare', icon: '⭐' };
      default:
        return { bg: 'rgba(255,255,255,0.2)', label: 'Common', icon: '' };
    }
  };
  
  // Fetch tracks from Discogs
  const fetchTracks = useCallback(async () => {
    setIsLoading(true);
    try {
      let allTracks = [];
      
      if (selectedGenre === 'All Genres') {
        // For "All Genres", fetch from multiple random electronic styles to get diverse mix
        const shuffledStyles = [...ALL_ELECTRONIC_STYLES].sort(() => Math.random() - 0.5);
        const stylesToFetch = shuffledStyles.slice(0, 5); // Pick 5 random styles
        
        // Fetch from each style
        for (const style of stylesToFetch) {
          const requestBody = {
            query: '',
            limit: 25,
            genres: [style],
            all_electronic_styles: ALL_ELECTRONIC_STYLES // Send full list for validation
          };
          
          // Add year range filter if decade selected
          if (selectedDecade !== 'Since Forever') {
            const year = parseInt(selectedDecade.replace('s', ''));
            requestBody.year_from = year;
            requestBody.year_to = year + 9;
          }
          
          // Add country filter
          if (selectedCountry !== 'Everywhere') {
            requestBody.country = selectedCountry;
          }
          
          try {
            const response = await fetch(`${BACKEND_URL}/api/discogs/search`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(requestBody)
            });
            
            if (response.ok) {
              const data = await response.json();
              allTracks = [...allTracks, ...(data.results || [])];
            }
          } catch (err) {
            console.warn(`Failed to fetch ${style}:`, err);
          }
        }
        
        // Shuffle the combined results for variety
        allTracks = allTracks.sort(() => Math.random() - 0.5);
        
      } else {
        // Single genre selected
        const requestBody = {
          query: '',
          limit: 100,
          genres: [selectedGenre],
          all_electronic_styles: ALL_ELECTRONIC_STYLES
        };
        
        // Add year range filter if decade selected
        if (selectedDecade !== 'Since Forever') {
          const year = parseInt(selectedDecade.replace('s', ''));
          requestBody.year_from = year;
          requestBody.year_to = year + 9;
        }
        
        // Add country filter
        if (selectedCountry !== 'Everywhere') {
          requestBody.country = selectedCountry;
        }
        
        const response = await fetch(`${BACKEND_URL}/api/discogs/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });
        
        if (response.ok) {
          const data = await response.json();
          allTracks = data.results || [];
        }
      }
      
      // Transform and add rarity scores
      let transformedTracks = allTracks.map(track => ({
        ...track,
        rarity: calculateRarity(track),
        price: track.lowest_price || Math.floor(Math.random() * 50) + 5,
        copies_available: track.num_for_sale || Math.floor(Math.random() * 20)
      }));
      
      // Remove duplicates by ID
      const seen = new Set();
      transformedTracks = transformedTracks.filter(track => {
        if (seen.has(track.id)) return false;
        seen.add(track.id);
        return true;
      });
      
      // Filter by rarity if selected
      if (selectedRarity !== 'all') {
        transformedTracks = transformedTracks.filter(t => t.rarity === selectedRarity);
      }
      
      setTracks(transformedTracks);
      
      // Initialize likes for all tracks to prevent re-render count changes
      const initialLikes = {};
      transformedTracks.forEach(track => {
        // Use deterministic hash for initial count
        let hash = 0;
        const str = String(track.id);
        for (let i = 0; i < str.length; i++) {
          const char = str.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash;
        }
        initialLikes[track.id] = { liked: false, count: Math.abs(hash % 50) };
      });
      setTrackLikes(prev => ({ ...initialLikes, ...prev })); // Preserve any existing likes
      
      // Set initial track for station mode
      if (transformedTracks.length > 0 && !currentTrack) {
        setCurrentTrack(transformedTracks[0]);
        if (transformedTracks.length > 1) {
          setNextTrack(transformedTracks[1]);
        }
      }
    } catch (error) {
      console.error('Error fetching tracks:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedGenre, selectedCountry, selectedDecade, selectedRarity, currentTrack]);

  // Search Spotify for track (PRIMARY)
  const searchSpotify = async (track) => {
    try {
      const query = `${track.artist || ''} ${track.title || ''}`.trim();
      const response = await fetch(`${BACKEND_URL}/api/spotify/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, limit: 1 })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.tracks && data.tracks.length > 0) {
          const spotifyTrack = data.tracks[0];
          return {
            uri: spotifyTrack.uri,
            name: spotifyTrack.name,
            artist: spotifyTrack.artists?.join(', '),
            duration_ms: spotifyTrack.duration_ms,
            image: spotifyTrack.image_url
          };
        }
      }
    } catch (error) {
      console.error('Spotify search error:', error);
    }
    return null;
  };

  // Search YouTube for track (FALLBACK)
  const searchYouTube = async (track) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/youtube/crates/search?artist=${encodeURIComponent(track.artist || '')}&title=${encodeURIComponent(track.title || '')}`);
      if (response.ok) {
        const data = await response.json();
        if (data.found && data.video_id) {
          return data.video_id;
        }
      }
    } catch (error) {
      console.error('YouTube search error:', error);
    }
    return null;
  };

  // Crate Finder - Cross-platform search (Spotify + YouTube + Discogs simultaneously)
  const searchCrateFinder = async () => {
    if (!crateFinderQuery.trim()) return;
    
    setIsSearchingCrateFinder(true);
    setCrateFinderSearched(false);
    setCrateFinderResults({ spotify: [], youtube: [], discogs: [] });
    
    try {
      // Search all platforms in parallel
      const [spotifyRes, youtubeRes, discogsRes] = await Promise.allSettled([
        // Spotify search
        fetch(`${BACKEND_URL}/api/spotify/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: crateFinderQuery, limit: 8 })
        }).then(r => r.ok ? r.json() : { tracks: [] }),
        
        // YouTube search (using the crates endpoint with query as title)
        fetch(`${BACKEND_URL}/api/youtube/crates/search?artist=&title=${encodeURIComponent(crateFinderQuery)}`).then(r => r.ok ? r.json() : { found: false }),
        
        // Discogs search
        fetch(`${BACKEND_URL}/api/discogs/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: crateFinderQuery, limit: 8 })
        }).then(r => r.ok ? r.json() : { results: [] })
      ]);
      
      const results = {
        spotify: spotifyRes.status === 'fulfilled' ? (spotifyRes.value.tracks || []) : [],
        youtube: youtubeRes.status === 'fulfilled' && youtubeRes.value.found ? [youtubeRes.value] : [],
        discogs: discogsRes.status === 'fulfilled' ? (discogsRes.value.results || []) : []
      };
      
      setCrateFinderResults(results);
      setCrateFinderSearched(true);
    } catch (error) {
      console.error('Crate Finder search error:', error);
      setCrateFinderSearched(true);
    } finally {
      setIsSearchingCrateFinder(false);
    }
  };

  // Handle Crate Finder track selection (converts to common format)
  const handleCrateFinderTrackSelect = (track, source) => {
    let normalizedTrack;
    
    if (source === 'spotify') {
      normalizedTrack = {
        id: track.id,
        title: track.name,
        artist: track.artists?.join(', ') || 'Unknown Artist',
        thumb: track.image_url,
        cover_image: track.image_url,
        year: track.release_date?.substring(0, 4),
        spotify_uri: track.uri,
        duration_ms: track.duration_ms,
        source: 'spotify',
        rarity: 'common'
      };
    } else if (source === 'youtube') {
      normalizedTrack = {
        id: `yt_${track.video_id}`,
        title: track.title || crateFinderQuery,
        artist: track.artist || 'Unknown Artist',
        thumb: `https://img.youtube.com/vi/${track.video_id}/mqdefault.jpg`,
        cover_image: `https://img.youtube.com/vi/${track.video_id}/maxresdefault.jpg`,
        youtube_id: track.video_id,
        source: 'youtube',
        rarity: 'common'
      };
    } else if (source === 'discogs') {
      normalizedTrack = {
        ...track,
        title: track.title?.split(' - ')[1] || track.title,
        artist: track.title?.split(' - ')[0] || 'Unknown Artist',
        source: 'discogs',
        rarity: calculateRarity(track)
      };
    }
    
    handleTrackSelect(normalizedTrack);
  };

  // Play Crate Finder result directly (with known source)
  const playCrateFinderTrack = async (track, source) => {
    handleCrateFinderTrackSelect(track, source);
    
    // Auto-play based on source
    setTimeout(async () => {
      if (source === 'spotify' && track.uri) {
        if (isSpotifyReady) {
          setSpotifyTrackUri(track.uri);
          setPlaybackSource('spotify');
          setDuration((track.duration_ms || 0) / 1000);
          const success = await spotifyPlayer.play(track.uri);
          if (success) {
            setIsPlaying(true);
          }
        } else {
          setShowSpotifyPrompt(true);
        }
      } else if (source === 'youtube' && track.video_id) {
        setCurrentVideoId(track.video_id);
        setPlaybackSource('youtube');
        setIsPlaying(true);
      }
    }, 100);
  };

  // Handle track selection
  const handleTrackSelect = async (track) => {
    // Save current track to history before switching
    if (currentTrack) {
      setTrackHistory(prev => [...prev, currentTrack]);
      setPreviousTrack(currentTrack);
    }
    
    // Stop current playback
    if (playbackSource === 'spotify' && spotifyPlayer) {
      try {
        await spotifyPlayer.pause();
      } catch (e) {
        // Ignore
      }
    }
    if (youtubePlayerRef.current) {
      try {
        youtubePlayerRef.current.stopVideo();
      } catch (e) {
        // Ignore errors when stopping video
      }
    }
    
    setCurrentTrack(track);
    setIsPlaying(false);
    setIsLoadingAudio(false);
    setCurrentVideoId(null);
    setSpotifyTrackUri(null);
    setPlaybackSource(null);
    setAudioUnavailable(false);
    setYoutubePlayerReady(false); // Reset YouTube player state
    setCurrentTime(0); // Reset time
    setDuration(0); // Reset duration
    
    // Find next track
    const currentIndex = tracks.findIndex(t => t.id === track.id);
    if (currentIndex < tracks.length - 1) {
      setNextTrack(tracks[currentIndex + 1]);
    } else {
      setNextTrack(tracks[0]);
    }
    
    // Don't auto-search - wait for user to press play
    return track; // Return the track for chaining
  };

  // Handle select and immediately play - fixes race condition with setTimeout
  const handleSelectAndPlay = async (track) => {
    console.log('🎵 handleSelectAndPlay called for:', track?.title);
    
    // First select the track and wait for state to settle
    await handleTrackSelect(track);
    
    // Use a small delay to allow React state to update, then trigger play
    // This is more reliable than the previous 100ms timeout
    await new Promise(resolve => setTimeout(resolve, 150));
    
    // Now trigger playback - handlePlayPause will search for audio source
    await handlePlayPause();
  };

  // Handle play/pause - HYBRID: Spotify first, YouTube fallback
  const handlePlayPause = async () => {
    console.log('🎵 handlePlayPause called', { isPlaying, playbackSource, currentTrack: currentTrack?.title });
    
    // If already playing, just pause
    if (isPlaying) {
      console.log('⏸ Pausing playback, source:', playbackSource);
      if (playbackSource === 'spotify' && spotifyPlayer) {
        await spotifyPlayer.pause();
      } else if (playbackSource === 'youtube' && youtubePlayerRef.current) {
        youtubePlayerRef.current.pauseVideo();
      }
      setIsPlaying(false);
      stopTimeTracking();
      return;
    }
    
    // If we have a source loaded, resume playback
    if (playbackSource === 'spotify' && spotifyTrackUri) {
      console.log('▶️ Resuming Spotify playback');
      if (spotifyPlayer) {
        await spotifyPlayer.play(spotifyTrackUri);
        setIsPlaying(true);
        startTimeTracking('spotify');
      }
      return;
    }
    
    if (playbackSource === 'youtube' && currentVideoId) {
      console.log('▶️ Resuming YouTube playback');
      if (youtubePlayerRef.current) {
        youtubePlayerRef.current.playVideo();
        // YouTube time tracking will restart in onYouTubeStateChange
      }
      setIsPlaying(true);
      return;
    }
    
    // No audio loaded yet - search for it
    if (!currentTrack || isLoadingAudio) {
      console.log('⚠️ No track or already loading');
      return;
    }
    
    console.log('🔍 Searching for audio...', { track: currentTrack.title, artist: currentTrack.artist });
    setIsLoadingAudio(true);
    setAudioUnavailable(false);
    
    // Check Spotify connection status
    console.log('🎵 Spotify status:', { 
      isConnected: isSpotifyConnected, 
      isReady: spotifyPlayer?.isReady, 
      isPremium: spotifyPlayer?.isPremium,
      isSpotifyReady 
    });
    
    // Check if Spotify is connected - only show prompt once per session
    const hasShownSpotifyPrompt = sessionStorage.getItem('shown_spotify_prompt');
    if (!isSpotifyConnected && !hasShownSpotifyPrompt) {
      // Show prompt to connect Spotify, but still try YouTube
      setShowSpotifyPrompt(true);
      sessionStorage.setItem('shown_spotify_prompt', 'true');
    }
    
    // STEP 1: Try Spotify first (if connected AND ready)
    if (isSpotifyReady) {
      console.log('🎵 Trying Spotify search...');
      const spotifyResult = await searchSpotify(currentTrack);
      console.log('🎵 Spotify result:', spotifyResult);
      if (spotifyResult) {
        setSpotifyTrackUri(spotifyResult.uri);
        setPlaybackSource('spotify');
        setDuration(spotifyResult.duration_ms / 1000);
        setIsLoadingAudio(false);
        setCurrentTime(0); // Reset time when new track starts
        
        // Play via Spotify
        console.log('▶️ Playing via Spotify:', spotifyResult.uri);
        const success = await spotifyPlayer.play(spotifyResult.uri);
        if (success) {
          setIsPlaying(true);
          // Start time tracking for Spotify
          startTimeTracking('spotify');
          return;
        }
        console.log('⚠️ Spotify play failed, trying YouTube...');
      }
    } else {
      console.log('⚠️ Spotify not ready, skipping to YouTube');
    }
    
    // STEP 2: YouTube fallback
    console.log('🎬 Trying YouTube search...');
    const videoId = await searchYouTube(currentTrack);
    console.log('🎬 YouTube result:', videoId);
    if (videoId) {
      setCurrentVideoId(videoId);
      setPlaybackSource('youtube');
      setIsLoadingAudio(false);
      setCurrentTime(0); // Reset time when new track starts
      setIsPlaying(true);
      console.log('▶️ Playing via YouTube:', videoId);
      // Note: YouTube time tracking starts in onYouTubeStateChange when state === 1 (playing)
      return;
    }
    
    // STEP 3: No audio found anywhere
    console.log('❌ No audio found on any platform');
    setIsLoadingAudio(false);
    setAudioUnavailable(true);
  };

  // Handle next track - auto-play if was already playing
  const handleNext = async () => {
    if (nextTrack) {
      const wasPlaying = isPlaying;
      
      // Stop current playback
      stopTimeTracking();
      if (playbackSource === 'spotify' && spotifyPlayer) {
        try {
          await spotifyPlayer.pause();
        } catch (e) {
          // Ignore pause errors
        }
      }
      if (youtubePlayerRef.current) {
        try {
          youtubePlayerRef.current.stopVideo();
        } catch (e) {
          // Ignore errors when stopping video
        }
      }
      
      // Set flag to auto-play after track selection
      if (wasPlaying) {
        shouldAutoPlayRef.current = true;
      }
      
      // Select next track (this will trigger the useEffect below)
      handleTrackSelect(nextTrack);
    }
  };
  
  // Handle previous track - auto-play if was already playing
  const handlePrevious = async () => {
    if (previousTrack) {
      const wasPlaying = isPlaying;
      
      // Stop current playback and time tracking
      stopTimeTracking();
      if (playbackSource === 'spotify' && spotifyPlayer) {
        try {
          await spotifyPlayer.pause();
        } catch (e) {
          // Ignore pause errors
        }
      }
      if (youtubePlayerRef.current) {
        try {
          youtubePlayerRef.current.stopVideo();
        } catch (e) {
          // Ignore errors when stopping video
        }
      }
      
      // Set flag to auto-play after track selection
      if (wasPlaying) {
        shouldAutoPlayRef.current = true;
      }
      
      // Don't add to history when going back
      setCurrentTrack(previousTrack);
      setIsPlaying(false);
      setIsLoadingAudio(false);
      setCurrentVideoId(null);
      setSpotifyTrackUri(null);
      setPlaybackSource(null);
      setAudioUnavailable(false);
      setCurrentTime(0);
      setDuration(0);
      
      // Get the previous track from history
      const newHistory = [...trackHistory];
      newHistory.pop();
      setTrackHistory(newHistory);
      setPreviousTrack(newHistory.length > 0 ? newHistory[newHistory.length - 1] : null);
      
      // Update next track
      const currentIndex = tracks.findIndex(t => t.id === previousTrack.id);
      if (currentIndex < tracks.length - 1) {
        setNextTrack(tracks[currentIndex + 1]);
      } else {
        setNextTrack(tracks[0]);
      }
    }
  };
  
  // Stop time tracking interval (defined first to avoid hoisting issues)
  const stopTimeTracking = useCallback(() => {
    if (timeIntervalRef.current) {
      console.log('⏱️ Stopping time tracking');
      clearInterval(timeIntervalRef.current);
      timeIntervalRef.current = null;
    }
  }, []);

  // Start time tracking interval (for both YouTube and Spotify)
  const startTimeTracking = useCallback((source) => {
    // Clear any existing interval first
    if (timeIntervalRef.current) {
      clearInterval(timeIntervalRef.current);
      timeIntervalRef.current = null;
    }
    
    console.log('⏱️ Starting time tracking for:', source);
    
    // Small delay to let the player fully initialize before polling
    setTimeout(() => {
      timeIntervalRef.current = setInterval(async () => {
        if (source === 'youtube' && youtubePlayerRef.current) {
          try {
            const time = youtubePlayerRef.current.getCurrentTime();
            if (typeof time === 'number' && !isNaN(time) && time >= 0) {
              setCurrentTime(time);
            }
          } catch (e) {
            // Player not ready
          }
        } else if (source === 'spotify' && spotifyPlayer?.player) {
          // Use Spotify Web Playback SDK's getCurrentState() for accurate position
          try {
            const state = await spotifyPlayer.player.getCurrentState();
            if (state && state.position !== undefined) {
              const positionSec = state.position / 1000;
              setCurrentTime(positionSec);
              
              // Check if track ended
              // Track ended when: paused, position is 0, and we have previous tracks (meaning we just finished one)
              // OR: paused and position is at/near the duration
              const trackDuration = state.duration / 1000;
              const nearEnd = trackDuration > 0 && positionSec >= trackDuration - 1;
              
              if (state.paused && (nearEnd || (state.position === 0 && state.track_window?.previous_tracks?.length > 0))) {
                console.log('🎵 Spotify track ended, playing next...');
                // Use shouldAutoPlayRef to trigger auto-play
                shouldAutoPlayRef.current = true;
                stopTimeTracking();
                if (nextTrack) {
                  handleTrackSelect(nextTrack);
                }
              }
            }
          } catch (e) {
            // Player not ready or error
          }
        }
      }, 250);
    }, 500);
  }, [spotifyPlayer, nextTrack, stopTimeTracking, handleTrackSelect]);

  // YouTube player event handlers (hidden player - audio only fallback)
  const onYouTubeReady = (event) => {
    console.log('🎬 YouTube player ready');
    youtubePlayerRef.current = event.target;
    setYoutubePlayerReady(true);
    event.target.setVolume(volume * 100);
    // Start playing when ready if isPlaying is true and source is youtube
    if (isPlaying && playbackSource === 'youtube') {
      console.log('🎬 Auto-starting YouTube playback');
      event.target.playVideo();
    }
  };
  
  const onYouTubeStateChange = (event) => {
    // YouTube states: -1 unstarted, 0 ended, 1 playing, 2 paused, 3 buffering, 5 video cued
    console.log('🎬 YouTube state changed:', event.data, { isPlaying, playbackSource });
    if (event.data === 1) {
      console.log('🎬 YouTube playing');
      setIsPlaying(true);
      const videoDuration = event.target.getDuration();
      console.log('🎬 Video duration:', videoDuration);
      setDuration(videoDuration);
      
      // ALWAYS start from 0 for a fresh track - don't trust YouTube's cached position
      // Only use YouTube's time if we're resuming (currentTime > 0 means we paused earlier)
      if (currentTime === 0) {
        // Fresh track - force start from beginning
        console.log('🎬 Fresh track - starting from 0');
        setCurrentTime(0);
      } else {
        // Resuming - get actual position
        const currentVideoTime = event.target.getCurrentTime();
        console.log('🎬 Resuming from:', currentVideoTime);
        setCurrentTime(currentVideoTime || 0);
      }
      
      // Start the time tracking interval NOW that we know YouTube is playing
      startTimeTracking('youtube');
    } else if (event.data === 2) {
      console.log('🎬 YouTube paused');
      setIsPlaying(false);
      stopTimeTracking();
    } else if (event.data === 0) {
      // Audio ended - auto play next
      console.log('🎬 YouTube ended');
      setIsPlaying(false);
      setCurrentTime(0);
      setYoutubePlayerReady(false);
      stopTimeTracking();
      if (autoPlay && nextTrack) {
        handleNext();
      }
    } else if (event.data === 3) {
      // Buffering
      console.log('🎬 YouTube buffering...');
    }
  };
  
  // Seek to position
  const handleSeek = async (seekTime) => {
    if (!duration || duration === 0) {
      console.log('⚠️ Cannot seek - no duration');
      return;
    }
    console.log('⏩ Seeking to:', seekTime, 'of', duration);
    
    if (playbackSource === 'spotify' && spotifyPlayer?.player) {
      try {
        // Use the SDK's seek method directly (more reliable than API)
        await spotifyPlayer.player.seek(seekTime * 1000);
        setCurrentTime(seekTime);
        console.log('✅ Spotify seek completed');
      } catch (e) {
        console.error('❌ Spotify seek failed:', e);
        // Fallback to API seek
        await spotifyPlayer.seek(seekTime * 1000);
        setCurrentTime(seekTime);
      }
    } else if (playbackSource === 'youtube' && youtubePlayerRef.current) {
      try {
        // For YouTube, seekTo then update time after a small delay
        youtubePlayerRef.current.seekTo(seekTime, true);
        // Give YouTube a moment to seek, then update time
        setTimeout(() => {
          if (youtubePlayerRef.current) {
            const actualTime = youtubePlayerRef.current.getCurrentTime();
            setCurrentTime(actualTime || seekTime);
          }
        }, 100);
        console.log('✅ YouTube seek completed');
      } catch (e) {
        console.error('❌ YouTube seek failed:', e);
        setCurrentTime(seekTime);
      }
    }
  };
  
  // Update volume when changed
  useEffect(() => {
    // Set YouTube volume
    if (youtubePlayerRef.current) {
      youtubePlayerRef.current.setVolume(volume * 100);
    }
    // Set Spotify volume
    if (spotifyPlayer?.player) {
      spotifyPlayer.player.setVolume(volume).catch(e => {
        console.warn('Could not set Spotify volume:', e);
      });
    }
  }, [volume, spotifyPlayer]);

  // Cleanup time tracking on unmount
  useEffect(() => {
    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
    };
  }, []);

  // Auto-play when track changes and shouldAutoPlayRef is set
  useEffect(() => {
    if (currentTrack && shouldAutoPlayRef.current) {
      shouldAutoPlayRef.current = false;
      // Wait for state to settle, then trigger playback
      const timer = setTimeout(() => {
        console.log('🎵 Auto-playing next track...');
        handlePlayPause();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentTrack]);

  // MusicBrainz enrichment: fetch release/label/tag data when track changes
  useEffect(() => {
    if (!currentTrack?.title || !currentTrack?.artist) {
      setMbTrackData(null);
      return;
    }
    let cancelled = false;
    setMbLoading(true);
    setMbTrackData(null);

    const fetchMb = async () => {
      try {
        const params = new URLSearchParams({ title: currentTrack.title, artist: currentTrack.artist });
        const res = await fetch(`${BACKEND_URL}/api/musicbrainz/recording/search?${params}`);
        const json = await res.json();
        if (cancelled) return;
        if (json.success && json.data) {
          const data = { ...json.data };
          // Fetch relationships if we got an MBID
          if (data.mbid) {
            try {
              const relRes = await fetch(`${BACKEND_URL}/api/musicbrainz/recording/${data.mbid}/relationships`);
              const relJson = await relRes.json();
              if (!cancelled && relJson.success && relJson.data) {
                data.relationships = relJson.data;
              }
            } catch (_) {}
          }
          if (!cancelled) setMbTrackData(data);
        }
      } catch (_) {}
      if (!cancelled) setMbLoading(false);
    };

    fetchMb();
    return () => { cancelled = true; };
  }, [currentTrack?.title, currentTrack?.artist]);

  // Handle adding to wishlist - memoized to prevent re-renders
  const handleAddToWishlist = useCallback((track) => {
    if (onAddToWishlist) {
      onAddToWishlist({
        item_type: 'track',
        item_data: {
          id: track.id,
          title: track.title,
          artist: track.artist,
          year: track.year,
          genre: track.genre,
          artwork: track.thumb,
          discogs_url: `https://www.discogs.com/release/${track.id}`,
          price: track.price,
          rarity: track.rarity
        }
      });
    }
  }, [onAddToWishlist]);

  // Fetch user's crates for Add to Crate modal
  const fetchUserCrates = useCallback(async () => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) return;
    
    setIsLoadingCrates(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/crates?status=active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserCrates(data.crates || []);
      }
    } catch (err) {
      console.error('Error fetching crates:', err);
    } finally {
      setIsLoadingCrates(false);
    }
  }, []);

  // Handle opening Add to Crate modal
  const handleOpenAddToCrate = useCallback((track) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please log in to add tracks to your crates');
      return;
    }
    setShowAddToCrate(track);
    fetchUserCrates();
  }, [fetchUserCrates]);

  // Handle adding track to a specific crate
  const handleAddToCrate = useCallback(async (crateId, track) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) return;

    setAddingToCrate(crateId);
    try {
      // Determine source based on track data
      let source = 'discogs';
      let trackId = track.id;
      
      if (track.spotify_uri) {
        source = 'spotify';
        trackId = track.spotify_uri;
      } else if (track.youtube_id) {
        source = 'youtube';
        trackId = track.youtube_id;
      }

      const trackData = {
        track_id: String(trackId),
        source: source,
        title: track.title || track.name || 'Unknown Title',
        artist: track.artist || track.artists?.join(', ') || 'Unknown Artist',
        album: track.album || track.label || null,
        duration_ms: track.duration_ms || null,
        bpm: track.bpm || null,
        key: track.key || null,
        energy: track.energy || null,
        artwork_url: track.thumb || track.artwork || track.image || null,
        external_url: track.discogs_url || track.external_url || track.url || null,
        preview_url: track.preview_url || null
      };

      const response = await fetch(`${BACKEND_URL}/api/crates/${crateId}/tracks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(trackData)
      });

      if (response.ok) {
        // Success feedback
        const crate = userCrates.find(c => c.id === crateId);
        alert(`Added "${trackData.title}" to ${crate?.name || 'crate'}!`);
        setShowAddToCrate(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add track to crate');
      }
    } catch (err) {
      console.error('Error adding to crate:', err);
      alert('Failed to add track to crate');
    } finally {
      setAddingToCrate(null);
    }
  }, [userCrates]);

  // Generate stable initial like counts for tracks (using track ID as seed)
  const getInitialLikeCount = useCallback((trackId) => {
    // Use track ID to generate a deterministic "random" number
    let hash = 0;
    const str = String(trackId);
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash % 50); // 0-49 range
  }, []);

  // Handle like toggle - memoized to prevent re-renders
  const handleLike = useCallback((trackId) => {
    setTrackLikes(prev => {
      const initialCount = getInitialLikeCount(trackId);
      const current = prev[trackId] || { liked: false, count: initialCount };
      return {
        ...prev,
        [trackId]: {
          liked: !current.liked,
          count: current.liked ? current.count - 1 : current.count + 1
        }
      };
    });
  }, [getInitialLikeCount]);

  // Handle favorite toggle - memoized to prevent re-renders
  const handleFavorite = useCallback((trackId) => {
    setTrackFavorites(prev => ({
      ...prev,
      [trackId]: !prev[trackId]
    }));
  }, []);

  // Handle reaction - memoized to prevent re-renders
  const handleReaction = useCallback((trackId, emoji) => {
    setTrackReactions(prev => {
      const trackEmojis = prev[trackId] || {};
      const currentCount = trackEmojis[emoji] || 0;
      return {
        ...prev,
        [trackId]: {
          ...trackEmojis,
          [emoji]: currentCount + 1
        }
      };
    });
    setShowReactionPicker(null);
  }, []);

  // Handle share to user - memoized to prevent re-renders
  const handleShareToUser = useCallback((track) => {
    // This would open a user picker modal in a full implementation
    const shareText = `🎵 Check out "${track.title}" by ${track.artist} on Beatseek!`;
    navigator.clipboard.writeText(shareText);
    alert('Track info copied! Share with other Beatseek users.');
  }, []);

  // Handle Discord share - opens the Discord share modal - memoized to prevent re-renders
  const handleDiscordShare = useCallback((track) => {
    // Format track data for DiscordShareModal
    setDiscordShareTrack({
      title: track.title,
      name: track.title,
      artist: track.artist,
      artwork: track.thumb || getImageUrl(track),
      genre: Array.isArray(track.genre) ? track.genre[0] : track.genre,
      year: track.year,
      label: track.label,
      country: track.country
    });
  }, []);

  // Default likes object - single reference to prevent unnecessary re-renders
  const DEFAULT_LIKES = useMemo(() => ({ liked: false, count: 24 }), []);

  // ===== MEMOIZED TRACK SELECTORS FOR TAB DIFFERENTIATION =====
  
  // "The Dig" - All electronic tracks from the search (no additional filter)
  const digTracks = useMemo(() => {
    // Return all tracks - the backend already applies electronic music filter
    return tracks;
  }, [tracks]);

  // "Hot Picks / Discogs Top 25" - Sort by calculated popularity score, take top 25
  const hotPicksTracks = useMemo(() => {
    if (tracks.length === 0) return [];
    
    // Calculate a popularity/interest score for each track
    // Since Discogs API doesn't give us want/have stats in search, we use:
    // - Year (newer releases tend to be more "hot")
    // - Presence of format info (better cataloged = more popular)
    // - Random factor seeded by track ID for variety
    const scoredTracks = tracks.map(track => {
      const year = parseInt(track.year) || 1990;
      const currentYear = new Date().getFullYear();
      const recencyScore = Math.max(0, 100 - (currentYear - year)); // Newer = higher score
      
      // Use track ID to generate deterministic "popularity" variance
      let hash = 0;
      const str = String(track.id);
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash = hash & hash;
      }
      const popularityVariance = Math.abs(hash % 50); // 0-49 variance
      
      return {
        ...track,
        _hotScore: recencyScore + popularityVariance
      };
    });
    
    // Sort by hot score (highest first) and take top 25
    return scoredTracks
      .sort((a, b) => b._hotScore - a._hotScore)
      .slice(0, 25);
  }, [tracks]);

  // "Gems" - Vintage tracks (1999 and before) with randomization for variety
  const gemsTracks = useMemo(() => {
    if (tracks.length === 0) return [];
    
    // Filter for vintage tracks (year <= 1999)
    const vintageTracks = tracks.filter(track => {
      const year = parseInt(track.year);
      return year && year <= 1999;
    });
    
    // If we have vintage tracks, shuffle them for variety
    if (vintageTracks.length > 0) {
      // Fisher-Yates shuffle with seeded randomness based on current date
      // This gives consistent results within the same session but varies daily
      const shuffled = [...vintageTracks];
      const dateSeed = new Date().toDateString();
      let seedHash = 0;
      for (let i = 0; i < dateSeed.length; i++) {
        seedHash = ((seedHash << 5) - seedHash) + dateSeed.charCodeAt(i);
        seedHash = seedHash & seedHash;
      }
      
      // Simple seeded shuffle
      for (let i = shuffled.length - 1; i > 0; i--) {
        seedHash = (seedHash * 1103515245 + 12345) & 0x7fffffff;
        const j = seedHash % (i + 1);
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      
      // Mark all as gems for display purposes
      return shuffled.map(track => ({
        ...track,
        rarity: 'gem' // Override rarity for visual consistency
      }));
    }
    
    // Fallback: if no vintage tracks found, show tracks marked as rare/gem by the original algorithm
    return tracks.filter(t => t.rarity === 'gem' || t.rarity === 'rare');
  }, [tracks]);

  // Use ref to access trackLikes without adding it as a dependency
  const trackLikesRef = useRef(trackLikes);
  trackLikesRef.current = trackLikes;

  // Stable getTrackLikes function that doesn't change reference
  const getTrackLikes = useCallback((trackId) => {
    // First check state (user interactions are stored here) via ref
    if (trackLikesRef.current[trackId]) {
      return trackLikesRef.current[trackId];
    }
    
    // Then check ref cache (persists across re-renders)
    if (likesCache.current[trackId]) {
      return likesCache.current[trackId];
    }
    
    // Store default in ref cache so same reference is returned
    likesCache.current[trackId] = DEFAULT_LIKES;
    
    return DEFAULT_LIKES;
  }, [DEFAULT_LIKES]);

  const renderTrackActions = (track, compact = false) => {
    const trackId = track.id;
    const likes = getTrackLikes(trackId);
    const isFavorite = trackFavorites[trackId] || false;
    const reactions = trackReactions[trackId] || {};
    
    return (
      <div className={`track-emote-actions ${compact ? 'compact' : ''}`}>
        {/* Like */}
        <button 
          className={`emote-btn ${likes.liked ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); handleLike(trackId); }}
          title="Like"
        >
          <span>{likes.liked ? '👍' : '👍'}</span>
          {likes.count > 0 && <span className="emote-count">{likes.count}</span>}
        </button>

        {/* Favorite/Heart */}
        <button 
          className={`emote-btn ${isFavorite ? 'active heart' : ''}`}
          onClick={(e) => { e.stopPropagation(); handleFavorite(trackId); }}
          title="Favorite"
        >
          <span>{isFavorite ? '❤️' : '🤍'}</span>
        </button>

        {/* Reaction */}
        <div className="emote-wrapper">
          <button 
            className="emote-btn"
            onClick={(e) => { e.stopPropagation(); setShowReactionPicker(showReactionPicker === trackId ? null : trackId); }}
            title="React"
          >
            <span>🔥</span>
            {Object.keys(reactions).length > 0 && (
              <span className="emote-count">{Object.values(reactions).reduce((a, b) => a + b, 0)}</span>
            )}
          </button>
          
          {showReactionPicker === trackId && (
            <div className="reaction-picker" onClick={(e) => e.stopPropagation()}>
              {reactionEmojis.map(emoji => (
                <button 
                  key={emoji} 
                  className="reaction-emoji"
                  onClick={() => handleReaction(trackId, emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Share to User */}
        <button 
          className="emote-btn"
          onClick={(e) => { e.stopPropagation(); handleShareToUser(track); }}
          title="Share to User"
        >
          <span>📤</span>
        </button>

        {/* Line break for mobile landscape */}
        <div className="action-line-break"></div>

        {/* Wishlist */}
        <button 
          className="emote-btn"
          onClick={(e) => { e.stopPropagation(); handleAddToWishlist(track); }}
          title="Add to Wishlist"
        >
          <span>⭐</span>
        </button>

        {/* Add to Crate */}
        <button 
          className="emote-btn crate-btn"
          onClick={(e) => { e.stopPropagation(); handleOpenAddToCrate(track); }}
          title="Add to Crate"
        >
          <span>📦</span>
        </button>

        {/* Discord Share - Using Discord Logo SVG */}
        <button 
          className="emote-btn discord"
          onClick={(e) => { e.stopPropagation(); handleDiscordShare(track); }}
          title="Share to Discord"
        >
          <svg width="16" height="16" viewBox="0 0 71 55" fill="#5865F2">
            <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
          </svg>
        </button>
      </div>
    );
  };

  // Fetch tracks on filter change
  useEffect(() => {
    fetchTracks();
  }, [selectedGenre, selectedCountry, selectedDecade, selectedRarity, fetchTracks]);

  // Auto-connect Spotify when modal opens if user has a token
  useEffect(() => {
    const autoConnectSpotify = async () => {
      const token = localStorage.getItem('spotify_access_token');
      if (token && spotifyPlayer && !spotifyPlayer.isReady) {
        console.log('🎵 Auto-connecting Spotify for Diggin\' in the Crates...');
        await spotifyPlayer.connect(token);
      }
    };
    
    autoConnectSpotify();
  }, [spotifyPlayer]);

  // Reset filters
  const resetFilters = () => {
    setSelectedGenre('All Genres');
    setSelectedCountry('Everywhere');
    setSelectedDecade('Since Forever');
    setSelectedRarity('all');
  };

  // Force refresh tracks (clears cache and refetches)
  const handleRefreshTracks = async () => {
    console.log('🔄 Force refreshing tracks...');
    setTracks([]); // Clear current tracks
    setCurrentTrack(null);
    setNextTrack(null);
    setPreviousTrack(null);
    setTrackHistory([]);
    setIsLoading(true);
    
    // Small delay to ensure state is updated before fetching
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Call fetchTracks
    await fetchTracks();
  };

  // ==========================================
  // TONEARM TOUCH HANDLERS (Mobile Interactive Needle Drop)
  // ==========================================
  
  const touchStartAngle = useRef(0);
  const touchStartX = useRef(0);
  
  const handleTonearmTouchStart = (e) => {
    if (!isMobile || !currentTrack) return;
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    
    // Start from current position
    const startAngle = isPlaying ? 18 : 0;
    touchStartAngle.current = startAngle;
    setTonearmDragAngle(startAngle);
    setIsDraggingTonearm(true);
    
    // Haptic feedback on touch start
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  };
  
  const handleTonearmTouchMove = (e) => {
    if (!isDraggingTonearm) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    
    // Calculate horizontal movement from start position
    // Moving left = negative deltaX = positive angle (towards vinyl)
    // Moving right = positive deltaX = negative angle (away from vinyl)
    const deltaX = touch.clientX - touchStartX.current;
    
    // Convert pixel movement to angle (negative because left = play)
    const angleChange = deltaX * -0.15;
    
    // Calculate new angle from starting position
    let newAngle = touchStartAngle.current + angleChange;
    
    // Constrain angle between 0 (rest) and 22 (max play position)
    newAngle = Math.max(0, Math.min(22, newAngle));
    
    setTonearmDragAngle(newAngle);
  };
  
  const handleTonearmTouchEnd = (e) => {
    if (!isDraggingTonearm) return;
    e.preventDefault();
    
    // Check if tonearm is in "play zone" (angle > 10 degrees = over vinyl)
    if (tonearmDragAngle > 10) {
      // Needle is over vinyl - play
      if (!isPlaying) {
        if (navigator.vibrate) {
          navigator.vibrate([20, 50, 30]); // Satisfying drop feedback
        }
        handlePlayPause(); // Actually trigger playback
      }
    } else {
      // Needle is lifted off - stop
      if (isPlaying) {
        if (navigator.vibrate) {
          navigator.vibrate(15); // Lift feedback
        }
        handlePlayPause(); // Actually trigger pause
      }
    }
    
    setTonearmDragAngle(0); // Reset drag angle
    setIsDraggingTonearm(false);
  };
  
  // Calculate tonearm rotation for mobile
  const getTonearmRotation = () => {
    if (isDraggingTonearm) {
      return tonearmDragAngle;
    }
    return isPlaying ? 18 : 0;
  };

  // Render spinning vinyl player (social actions moved outside to prevent re-render flash)
  const renderVinylPlayer = () => {
    console.log('🎵 Rendering vinyl player:', { currentTrack, isPlaying, isMobile });
    
    // Responsive vinyl sizes - balanced for landscape
    const vinylSize = isLandscape ? 160 : (isMobile ? 210 : 280);
    const vinylOffset = isMobile ? 0 : 20;
    const labelSize = isLandscape ? 64 : (isMobile ? 84 : 100);
    const centerHoleSize = isLandscape ? 9 : (isMobile ? 14 : 12);
    
    return (
    <div className="vinyl-player-section">
      {/* Powered by Discogs - landscape version */}
      {isLandscape && (
        <div className="powered-by-landscape">
          <span>powered by</span>
          <a href="https://www.discogs.com" target="_blank" rel="noopener noreferrer">
            <img src="https://upload.wikimedia.org/wikipedia/commons/6/69/Discogs_record_icon.svg" alt="Discogs" style={{width: '12px', height: '12px'}} />
            <span>Discogs</span>
          </a>
        </div>
      )}
      
      {/* Powered by Discogs - shows inside vinyl section on mobile portrait ONLY */}
      {isMobile && (
        <div className={`powered-by mobile-only ${isLandscape ? 'landscape-show' : ''}`}>
          <span className="powered-by-text">powered by</span>
          <a href="https://www.discogs.com" target="_blank" rel="noopener noreferrer" className="discogs-link">
            <img src="https://upload.wikimedia.org/wikipedia/commons/6/69/Discogs_record_icon.svg" alt="Discogs" className="discogs-logo" />
            <span className="discogs-name">Discogs</span>
          </a>
        </div>
      )}
      
      {/* Vinyl and tonearm wrapper - moves independently from track info */}
      <div className="vinyl-tonearm-wrapper">
        <div className="vinyl-container">
          {/* Vinyl Record - Using inline styles to fix rendering */}
          <div 
            className={isPlaying ? 'spinning' : ''}
            style={{
              width: `${vinylSize}px`,
              height: `${vinylSize}px`,
              borderRadius: '50%',
              background: 'radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.3) 0%, transparent 35%), radial-gradient(circle at 75% 75%, rgba(0, 255, 255, 0.12) 0%, transparent 35%), linear-gradient(135deg, #0a0a0a 0%, #252525 50%, #0a0a0a 100%)',
              position: 'absolute',
              left: `${vinylOffset}px`,
              top: `${vinylOffset}px`,
              boxShadow: isMobile 
                ? '0 0 0 4px #050505, 0 0 0 5px rgba(0, 255, 255, 0.5), 0 4px 20px rgba(0, 0, 0, 0.9)'
                : '0 0 0 8px #050505, 0 0 0 10px rgba(0, 255, 255, 0.5), 0 8px 40px rgba(0, 0, 0, 0.9), 0 0 100px rgba(0, 255, 255, 0.2), inset 0 0 80px rgba(0, 0, 0, 0.8)',
              animation: isPlaying ? 'spin 2s linear infinite' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
          {/* Vinyl Grooves */}
          <div style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: `repeating-radial-gradient(
              circle at center,
              transparent 0px,
              transparent 2px,
              rgba(0, 0, 0, 0.5) 3px,
              transparent 4px
            )`,
            pointerEvents: 'none'
          }}></div>
          
          {/* Highlight reflection */}
          <div style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 40%, rgba(255, 255, 255, 0.08) 100%)',
            pointerEvents: 'none'
          }}></div>
          
          {/* Vinyl Label */}
          <div style={{
            width: `${labelSize}px`,
            height: `${labelSize}px`,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2a2a4a 0%, #0d0d1a 100%)',
            border: isMobile ? '2px solid rgba(0, 255, 255, 0.5)' : '3px solid rgba(0, 255, 255, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 2,
            boxShadow: isMobile 
              ? '0 0 10px rgba(0, 255, 255, 0.4), inset 0 0 10px rgba(0, 0, 0, 0.5)'
              : '0 0 20px rgba(0, 255, 255, 0.4), inset 0 0 20px rgba(0, 0, 0, 0.5)'
          }}>
            {getImageUrl(currentTrack) ? (
              <img 
                src={getImageUrl(currentTrack)} 
                alt={currentTrack?.title} 
                onError={handleImageError}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
              />
            ) : (
              <span style={{ color: 'cyan', fontSize: isMobile ? '8px' : '12px', fontWeight: 'bold' }}>BEATSEEK</span>
            )}
          </div>
          
          {/* Center Hole */}
          <div style={{
            position: 'absolute',
            width: `${centerHoleSize}px`,
            height: `${centerHoleSize}px`,
            borderRadius: '50%',
            background: 'radial-gradient(circle, #000 0%, #222 100%)',
            border: isMobile ? '1px solid #333' : '2px solid #333',
            zIndex: 3,
            boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.8)'
          }}></div>
        </div>
        
        {/* Tonearm - Realistic design with touch interaction on mobile */}
        <div 
          ref={tonearmRef}
          className={`tonearm-container ${isDraggingTonearm ? 'dragging' : ''}`}
          style={{
            position: 'absolute',
            right: isLandscape ? '-35px' : (isMobile ? '-30px' : '-40px'),
            top: isLandscape ? '-10px' : (isMobile ? '-5px' : '5px'),
            transformOrigin: '85% 15px',
            transform: isMobile 
              ? `scale(${isLandscape ? 0.65 : 0.825}) rotate(${getTonearmRotation()}deg)`
              : (isPlaying ? 'rotate(28deg)' : 'rotate(0deg)'),
            transition: isDraggingTonearm ? 'none' : 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 10,
            cursor: isMobile ? 'grab' : 'default',
            touchAction: 'none'
          }}
          onTouchStart={handleTonearmTouchStart}
          onTouchMove={handleTonearmTouchMove}
          onTouchEnd={handleTonearmTouchEnd}
        >
          {/* Tonearm Pivot Base */}
          <div style={{
            position: 'absolute',
            right: '0',
            top: '0',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'radial-gradient(circle at 30% 30%, #666 0%, #333 50%, #1a1a1a 100%)',
            border: '3px solid #444',
            boxShadow: '0 6px 15px rgba(0, 0, 0, 0.7), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Pivot center */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: 'radial-gradient(circle at 30% 30%, #888 0%, #333 100%)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
            }}></div>
          </div>
          
          {/* Vertical arm section */}
          <div style={{
            position: 'absolute',
            right: '15px',
            top: '35px',
            width: '10px',
            height: '140px',
            background: 'linear-gradient(90deg, #555 0%, #2a2a2a 50%, #444 100%)',
            borderRadius: '5px',
            boxShadow: '3px 3px 8px rgba(0, 0, 0, 0.6)',
            transformOrigin: 'top center'
          }}></div>
          
          {/* Headshell */}
          <div style={{
            position: 'absolute',
            right: '5px',
            top: '170px',
            width: '30px',
            height: '45px',
            background: 'linear-gradient(135deg, #4a4a4a 0%, #1a1a1a 100%)',
            borderRadius: '4px 4px 2px 2px',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.6), inset 0 1px 2px rgba(255, 255, 255, 0.1)',
            transform: 'perspective(100px) rotateX(5deg)'
          }}>
            {/* Cartridge body */}
            <div style={{
              position: 'absolute',
              bottom: '8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '18px',
              height: '20px',
              background: 'linear-gradient(180deg, #333 0%, #111 100%)',
              borderRadius: '2px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
            }}></div>
            
            {/* Stylus/Needle */}
            <div style={{
              position: 'absolute',
              bottom: '-6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '2px',
              height: '10px',
              background: 'linear-gradient(180deg, #888 0%, #ccc 50%, #fff 100%)',
              borderRadius: '0 0 1px 1px',
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.5)'
            }}></div>
            
            {/* Stylus tip glow */}
            <div style={{
              position: 'absolute',
              bottom: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              background: 'rgba(0, 255, 255, 0.8)',
              boxShadow: '0 0 8px rgba(0, 255, 255, 0.8), 0 0 15px rgba(0, 255, 255, 0.4)'
            }}></div>
          </div>
        </div>
      </div>
      </div> {/* End vinyl-tonearm-wrapper */}
      
      {/* Drag hint - landscape version with inline styles */}
      {isLandscape && currentTrack && (
        <div className="tonearm-hint-landscape" style={{
          position: 'fixed',
          bottom: '70px',
          left: '80px',
          fontSize: '9px',
          color: 'rgba(0, 255, 255, 0.5)',
          zIndex: 500
        }}>
          tap play or drag needle to play
        </div>
      )}
      
      {/* Drag hint for mobile portrait - shows when not playing */}
      {isMobile && !isLandscape && !isPlaying && currentTrack && (
        <div className="tonearm-hint">
          <span>tap play or drag needle to play</span>
        </div>
      )}
      
      {/* Track Info - Positioned further right */}
      <div className="track-info-panel" style={{ marginLeft: '40px' }}>
        <h2 className="track-artist">{currentTrack?.artist || 'Various'}</h2>
        <div className="track-divider"></div>
        <h3 className="track-title">{currentTrack?.title || 'Select a track'}</h3>
        <div className="track-meta">
          <span className="track-year">{currentTrack?.year || '-'}</span>
          {currentTrack?.label && <span className="track-label">{currentTrack.label}</span>}
          {currentTrack?.country && <span className="track-country">{currentTrack.country}</span>}
        </div>
        {currentTrack?.genre && (
          <div className="track-genres">
            {(Array.isArray(currentTrack.genre) ? currentTrack.genre : [currentTrack.genre]).slice(0, 2).map((g, i) => (
              <span key={i} className="genre-tag">{g}</span>
            ))}
          </div>
        )}

        {/* MusicBrainz Release Intelligence */}
        {mbTrackData && (mbTrackData.tags?.length > 0 || mbTrackData.artist_credits?.length > 1 || mbTrackData.relationships?.remixes?.length > 0 || mbTrackData.relationships?.samples?.length > 0 || mbTrackData.relationships?.covers?.length > 0) && (
          <div data-testid="crates-mb-enrichment" style={{
            marginTop: '14px', padding: '12px 14px',
            background: 'linear-gradient(135deg, rgba(255,0,255,0.08), rgba(180,0,255,0.04))',
            border: '1px solid rgba(255,0,255,0.3)',
            borderRadius: '10px',
            borderLeft: '3px solid #ff00ff',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ff00ff', boxShadow: '0 0 8px rgba(255,0,255,0.6)' }} />
              <span style={{ fontSize: '9px', color: '#ff00ff', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px' }}>MusicBrainz Intelligence</span>
            </div>

            {/* Genre Tags — the primary value-add */}
            {mbTrackData.tags?.length > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {mbTrackData.tags.slice(0, 8).map((tag, i) => (
                    <span key={i} data-testid={`mb-tag-${i}`} style={{
                      padding: '3px 10px', fontSize: '10px',
                      fontWeight: i === 0 ? 700 : 500,
                      background: i === 0 ? 'rgba(255,0,255,0.25)' : 'rgba(255,0,255,0.1)',
                      border: `1px solid rgba(255,0,255,${i === 0 ? 0.5 : 0.22})`,
                      borderRadius: '12px', color: i === 0 ? '#ff44ff' : '#ff88ff',
                      letterSpacing: '0.3px',
                    }}>{tag.name}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Multi-artist Credits */}
            {mbTrackData.artist_credits?.length > 1 && (
              <div data-testid="mb-credits" style={{ fontSize: '11px', color: '#e080e0', marginBottom: '8px', padding: '4px 8px', background: 'rgba(255,0,255,0.06)', borderRadius: '6px' }}>
                <span style={{ fontWeight: 700, color: '#ff66ff' }}>Credits: </span>
                {mbTrackData.artist_credits.map(ac => ac.name + (ac.join_phrase || '')).join('')}
              </div>
            )}

            {/* Remix Relationships */}
            {mbTrackData.relationships?.remixes?.length > 0 && (
              <div data-testid="mb-remix" style={{ fontSize: '11px', color: '#ff66ff', marginBottom: '6px', padding: '4px 8px', background: 'rgba(255,0,255,0.06)', borderRadius: '6px', fontWeight: 600 }}>
                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#ff66ff', marginRight: '5px', verticalAlign: 'middle', boxShadow: '0 0 4px rgba(255,0,255,0.5)' }} />
                {mbTrackData.relationships.remixes[0].direction === 'backward'
                  ? <>Remix of <span style={{ color: '#fff' }}>"{mbTrackData.relationships.remixes[0].target_title}"</span> by {mbTrackData.relationships.remixes[0].target_artist}</>
                  : <>Remixed by <span style={{ color: '#fff' }}>{mbTrackData.relationships.remixes[0].target_artist}</span> as "{mbTrackData.relationships.remixes[0].target_title}"</>}
              </div>
            )}

            {/* Sample Relationships */}
            {mbTrackData.relationships?.samples?.length > 0 && (
              <div data-testid="mb-samples" style={{ fontSize: '11px', color: '#cc66ff', marginBottom: '6px', padding: '4px 8px', background: 'rgba(180,0,255,0.06)', borderRadius: '6px' }}>
                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#cc66ff', marginRight: '5px', verticalAlign: 'middle', boxShadow: '0 0 4px rgba(180,0,255,0.5)' }} />
                Samples <span style={{ color: '#fff', fontWeight: 600 }}>"{mbTrackData.relationships.samples[0].target_title}"</span> {mbTrackData.relationships.samples[0].target_artist && <>by {mbTrackData.relationships.samples[0].target_artist}</>}
              </div>
            )}

            {/* Cover Relationships */}
            {mbTrackData.relationships?.covers?.length > 0 && (
              <div data-testid="mb-covers" style={{ fontSize: '11px', color: '#bb66ee', padding: '4px 8px', background: 'rgba(150,0,255,0.06)', borderRadius: '6px' }}>
                {mbTrackData.relationships.covers[0].direction === 'backward'
                  ? <>Cover of <span style={{ color: '#fff', fontWeight: 600 }}>"{mbTrackData.relationships.covers[0].target_title}"</span></>
                  : <>Covered by <span style={{ color: '#fff', fontWeight: 600 }}>{mbTrackData.relationships.covers[0].target_artist}</span></>}
              </div>
            )}
          </div>
        )}
        {mbLoading && (
          <div style={{ marginTop: '8px', fontSize: '10px', color: '#ff66ff', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ff00ff', animation: 'pulse 1s infinite' }} />
            Loading MusicBrainz data...
          </div>
        )}
      </div>
      
      {/* Next Track Preview - Top Right (non-clickable sneak peek) */}
      {nextTrack && (
        <div className="next-track-preview">
          <span className="next-label">Next</span>
          <div className="next-thumb">
            {getImageUrl(nextTrack) ? (
              <img src={getImageUrl(nextTrack)} alt={nextTrack.title} onError={handleImageError} />
            ) : (
              <div className="default-thumb">🎵</div>
            )}
          </div>
          <div className="next-info">
            <span className="next-title">{nextTrack.title}</span>
            <span className="next-artist">{nextTrack.artist}</span>
          </div>
        </div>
      )}
    </div>
  );
  };

  // Render bottom player bar
  const renderBottomPlayer = () => (
    <div className="bottom-player-bar">
      {/* Hidden YouTube Player (audio only) - key forces recreation on video change */}
      {currentVideoId && (
        <div className="youtube-player-container">
          <YouTube
            key={`youtube-${currentVideoId}`}
            videoId={currentVideoId}
            opts={{
              height: '1',
              width: '1',
              playerVars: {
                autoplay: 1,
                controls: 0,
                disablekb: 1,
                fs: 0,
                modestbranding: 1,
                rel: 0,
                playsinline: 1,
                start: 0,
                origin: window.location.origin,
              },
            }}
            onReady={onYouTubeReady}
            onStateChange={onYouTubeStateChange}
            onError={(e) => {
              console.error('🎬 YouTube Player Error:', e);
              setAudioUnavailable(true);
              setIsLoadingAudio(false);
            }}
          />
        </div>
      )}
      
      {/* Spotify Connect Prompt */}
      {showSpotifyPrompt && !isSpotifyConnected && (
        <div className="spotify-connect-prompt">
          <span>🎵 Connect Spotify for best playback quality</span>
          <a href="/dashboard" className="connect-spotify-btn">Connect Spotify</a>
          <button className="dismiss-prompt" onClick={() => setShowSpotifyPrompt(false)}>×</button>
        </div>
      )}
      
      <div className="player-track-info">
        <div className="player-thumb">
          {getImageUrl(currentTrack) ? (
            <img src={getImageUrl(currentTrack)} alt="" onError={handleImageError} />
          ) : (
            <div className="default-player-thumb">🎵</div>
          )}
          {/* Playback source indicator */}
          {playbackSource && (
            <span className={`playback-source ${playbackSource}`}>
              {playbackSource === 'spotify' ? '♪' : '▶'}
            </span>
          )}
        </div>
        <div className="player-text">
          <span className="player-artist">{currentTrack?.artist || 'No track'}</span>
          <span className="player-title">{currentTrack?.title || 'Select a track to play'}</span>
          {/* Audio unavailable message */}
          {audioUnavailable && (
            <span className="audio-unavailable">
              💎 True crate find! Audio not on streaming platforms.
              {isDiscogsTrack(currentTrack) && (
                <a 
                  href={`https://www.discogs.com/release/${currentTrack?.id}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="discogs-link"
                >
                  Buy on Discogs →
                </a>
              )}
            </span>
          )}
        </div>
      </div>
      
      <div className="player-controls">
        <span className="player-time">{formatTime(currentTime)}</span>
        <div 
          className="progress-bar"
          onClick={(e) => {
            e.stopPropagation();
            if (!duration) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const percentage = clickX / rect.width;
            const seekTime = percentage * duration;
            handleSeek(seekTime);
          }}
        >
          <div className="progress-fill" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}></div>
          <div className="progress-handle" style={{ left: `${duration ? (currentTime / duration) * 100 : 0}%` }}></div>
        </div>
        <span className="player-time">{formatTime(duration)}</span>
        
        {/* Fresh isolated reactions component - key resets state on track change */}
        <PlayerReactions 
          key={currentTrack?.id || 'no-track'}
          trackId={currentTrack?.id}
          onDiscordShare={() => currentTrack && handleDiscordShare(currentTrack)}
          onWishlist={() => currentTrack && handleAddToWishlist(currentTrack)}
        />
        
        <button 
          className={`control-btn prev ${!previousTrack ? 'disabled' : ''}`} 
          onClick={(e) => { e.stopPropagation(); handlePrevious(); }}
          disabled={!previousTrack}
          title="Previous track"
        >
          ⏮
        </button>
        <button 
          className={`control-btn pause ${audioUnavailable ? 'unavailable' : ''}`}
          onClick={(e) => { e.stopPropagation(); handlePlayPause(); }} 
          disabled={isLoadingAudio || audioUnavailable}
          title={audioUnavailable ? 'Audio not available' : (isPlaying ? 'Pause' : 'Play')}
        >
          {isLoadingAudio ? '⏳' : (audioUnavailable ? '✗' : (isPlaying ? '⏸' : '▶'))}
        </button>
        <button 
          className="control-btn next" 
          onClick={(e) => { e.stopPropagation(); handleNext(); }} 
          title="Next track"
        >
          ⏭
        </button>
      </div>
      
      <div className="player-volume" onClick={(e) => e.stopPropagation()}>
        <span 
          className="volume-icon" 
          onClick={(e) => {
            e.stopPropagation();
            if (isMuted) {
              // Unmute - restore previous volume
              setVolume(volumeBeforeMute);
              setIsMuted(false);
            } else {
              // Mute - save current volume and set to 0
              setVolumeBeforeMute(volume);
              setVolume(0);
              setIsMuted(true);
            }
          }}
          style={{ cursor: 'pointer' }}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔈' : '🔊'}
        </span>
        <input 
          type="range" 
          min="0" 
          max="1" 
          step="0.01" 
          value={volume}
          onChange={(e) => {
            e.stopPropagation();
            const newVolume = parseFloat(e.target.value);
            setVolume(newVolume);
            setIsMuted(newVolume === 0);
            // Update YouTube player volume
            if (youtubePlayerRef.current) {
              youtubePlayerRef.current.setVolume(newVolume * 100);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          className="volume-slider"
        />
      </div>
    </div>
  );

  // Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Render Hot Picks content - uses memoized hotPicksTracks selector
  const renderHotPicks = () => {
    return (
      <div className="hot-picks-content">
        <div className="hot-picks-tabs">
          <button 
            className={`hp-tab ${hotPicksSubTab === 'discogs-top-25' || hotPicksSubTab === 'most-wanted' ? 'active' : ''}`}
            onClick={() => setHotPicksSubTab('discogs-top-25')}
          >
            Discogs Top 25
          </button>
          <button 
            className={`hp-tab ${hotPicksSubTab === 'beatseeker-picks' ? 'active' : ''}`}
            onClick={() => setHotPicksSubTab('beatseeker-picks')}
          >
            Beatseekers Discogs Top 25
            <span className="coming-soon-mini">Coming Soon</span>
          </button>
        </div>
        <p className="filter-hint">Use filter menu for sort options</p>
        
        {hotPicksSubTab === 'beatseeker-picks' ? (
          <div className="coming-soon-section">
            <div className="coming-soon-icon">🔥</div>
            <h3>Beatseekers Discogs Top 25</h3>
            <span className="coming-soon-badge">Coming Soon</span>
            <p>Top 25 tracks loved by the BEATSEEK community</p>
          </div>
        ) : (
          <div className="hot-picks-grid-container">
            {hotPicksTracks.map((track, index) => (
              <div 
                key={track.id} 
                className={`track-card ${currentTrack?.id === track.id ? 'playing' : ''}`}
                onClick={() => handleTrackSelect(track)}
              >
                <div className="crates-track-rank">#{index + 1}</div>
                <div className="crates-track-thumb">
                  {getImageUrl(track) ? (
                    <img src={getImageUrl(track)} alt={track.title} onError={handleImageError} />
                  ) : (
                    <div className="crates-default-track-thumb">🎵</div>
                  )}
                  {/* Quick Play Button */}
                  <button 
                    className="quick-play-btn"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (currentTrack?.id === track.id) {
                        // Same track - toggle play/pause
                        handlePlayPause();
                      } else {
                        // Different track - select and play with proper async handling
                        await handleSelectAndPlay(track);
                      }
                    }}
                  >
                    {currentTrack?.id === track.id && isPlaying ? '⏸' : '▶'}
                  </button>
                  {track.rarity !== 'common' && (
                    <div className="rarity-indicator" style={{ background: getRarityBadge(track.rarity).bg }}>
                      {getRarityBadge(track.rarity).icon}
                    </div>
                  )}
                </div>
                <div className="crates-track-card-info">
                  <span className="crates-card-title">{track.title}</span>
                  <span className="crates-card-artist">{track.artist}</span>
                  <span className="crates-card-year">{track.year || 'Unknown'}</span>
                  {/* Discogs Purchase Link - only show for Discogs-sourced tracks */}
                  {isDiscogsTrack(track) && (
                    <a 
                      href={`https://www.discogs.com/release/${track.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="discogs-buy-link"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        window.open(`https://www.discogs.com/release/${track.id}`, '_blank');
                      }}
                    >
                      Buy on Discogs →
                    </a>
                  )}
                </div>
                {renderTrackActions(track, true)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render Gems content - uses memoized gemsTracks selector (vintage tracks 1999 and before)
  const renderGems = () => (
    <div className="gems-content">
      <div className="gems-header">
        <h3>💎 Hidden Gems</h3>
        <p>Vintage electronic releases from 1999 and earlier</p>
      </div>
      <p className="filter-hint">Use filter menu for sort options</p>
      <div className="gems-grid-container">
        {gemsTracks.length > 0 ? (
          gemsTracks.map((track) => (
            <div 
              key={track.id} 
              className={`track-card ${currentTrack?.id === track.id ? 'playing' : ''}`}
              onClick={() => handleTrackSelect(track)}
            >
              <div className="crates-track-thumb">
                {getImageUrl(track) ? (
                  <img src={getImageUrl(track)} alt={track.title} onError={handleImageError} />
                ) : (
                  <div className="crates-default-track-thumb">🎵</div>
                )}
                {/* Quick Play Button */}
                <button 
                  className="quick-play-btn"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (currentTrack?.id === track.id) {
                      handlePlayPause();
                    } else {
                      await handleSelectAndPlay(track);
                    }
                  }}
                >
                  {currentTrack?.id === track.id && isPlaying ? '⏸' : '▶'}
                </button>
                <div className="rarity-badge" style={{ background: getRarityBadge(track.rarity).bg }}>
                  {getRarityBadge(track.rarity).icon} {getRarityBadge(track.rarity).label}
                </div>
              </div>
              <div className="crates-track-card-info">
                <span className="crates-card-title">{track.title}</span>
                <span className="crates-card-artist">{track.artist}</span>
                <span className="crates-card-year">{track.year || 'Unknown'}</span>
                {/* Discogs Purchase Link - only show for Discogs-sourced tracks */}
                {isDiscogsTrack(track) && (
                  <a 
                    href={`https://www.discogs.com/release/${track.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="discogs-buy-link"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      window.open(`https://www.discogs.com/release/${track.id}`, '_blank');
                    }}
                  >
                    Buy on Discogs →
                  </a>
                )}
              </div>
              {renderTrackActions(track, true)}
            </div>
          ))
        ) : (
          <div className="no-gems-message">
            <p>No vintage tracks found with current filters. Try adjusting genre or removing decade filter to discover hidden gems from the 90s and earlier!</p>
          </div>
        )}
      </div>
    </div>
  );

  // Render Track Search content (cross-platform search)
  const renderCrateFinder = () => (
    <div className="crate-finder-content">
      <div className="crate-finder-header">
        <h3>🔍 Track Search</h3>
        <p>Search across Spotify, YouTube & Discogs simultaneously</p>
      </div>
      
      {/* Search Bar */}
      <div className="crate-finder-search">
        <input 
          type="text"
          placeholder="Search artist, track, or release..."
          value={crateFinderQuery}
          onChange={(e) => setCrateFinderQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && searchCrateFinder()}
          className="crate-finder-input"
          data-testid="crate-finder-input"
        />
        <button 
          className="crate-finder-btn"
          onClick={searchCrateFinder}
          disabled={isSearchingCrateFinder || !crateFinderQuery.trim()}
          data-testid="crate-finder-search-btn"
        >
          {isSearchingCrateFinder ? '⏳ Searching...' : '🔍 Find'}
        </button>
      </div>
      <p className="filter-hint">Use filter menu for sort options</p>
      
      {/* Results */}
      {isSearchingCrateFinder ? (
        <div className="crate-finder-loading">
          <div className="loading-vinyl"></div>
          <p>Searching all platforms...</p>
        </div>
      ) : crateFinderSearched ? (
        <div className="crate-finder-results">
          {/* Discogs Results */}
          <div className="crate-finder-section">
            <div className="section-header discogs-header">
              <span className="platform-icon">📀</span>
              <h4>Discogs</h4>
              <span className="result-count">{crateFinderResults.discogs.length} results</span>
              <span className="physical-badge">💿 Physical</span>
            </div>
            {crateFinderResults.discogs.length > 0 ? (
              <div className="crate-finder-grid">
                {crateFinderResults.discogs.map((track) => (
                  <div 
                    key={track.id} 
                    className="crate-finder-card discogs-card"
                    onClick={() => handleCrateFinderTrackSelect(track, 'discogs')}
                    data-testid={`discogs-result-${track.id}`}
                  >
                    <div className="cf-card-thumb">
                      {getImageUrl(track) ? (
                        <img src={getImageUrl(track)} alt={track.title} onError={handleImageError} />
                      ) : (
                        <div className="cf-default-thumb">📀</div>
                      )}
                      {track.num_for_sale > 0 && (
                        <div className="for-sale-badge">{track.num_for_sale} for sale</div>
                      )}
                    </div>
                    <div className="cf-card-info">
                      <span className="cf-card-title">{track.title?.split(' - ')[1] || track.title}</span>
                      <span className="cf-card-artist">{track.title?.split(' - ')[0] || 'Various'}</span>
                      {isDiscogsTrack(track) && (
                        <a 
                          href={`https://www.discogs.com/release/${track.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="discogs-buy-link"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                          }}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            window.open(`https://www.discogs.com/release/${track.id}`, '_blank');
                          }}
                        >
                          Buy on Discogs →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-results">No Discogs results found</p>
            )}
          </div>
          
          {/* Spotify Results */}
          <div className="crate-finder-section">
            <div className="section-header spotify-header">
              <span className="platform-icon">🎵</span>
              <h4>Spotify</h4>
              <span className="result-count">{crateFinderResults.spotify.length} results</span>
              {crateFinderResults.spotify.length > 0 && (
                <span className="playback-badge">▶ Instant Play</span>
              )}
            </div>
            {crateFinderResults.spotify.length > 0 ? (
              <div className="crate-finder-grid">
                {crateFinderResults.spotify.map((track) => (
                  <div 
                    key={track.id} 
                    className="crate-finder-card spotify-card"
                    onClick={() => playCrateFinderTrack(track, 'spotify')}
                    data-testid={`spotify-result-${track.id}`}
                  >
                    <div className="cf-card-thumb">
                      {track.image_url ? (
                        <img src={track.image_url} alt={track.name} onError={handleImageError} />
                      ) : (
                        <div className="cf-default-thumb">🎵</div>
                      )}
                      <div className="play-overlay">▶</div>
                    </div>
                    <div className="cf-card-info">
                      <span className="cf-card-title">{track.name}</span>
                      <span className="cf-card-artist">{track.artists?.join(', ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-results">No Spotify results found</p>
            )}
          </div>
          
          {/* YouTube Results */}
          <div className="crate-finder-section">
            <div className="section-header youtube-header">
              <span className="platform-icon">▶️</span>
              <h4>YouTube</h4>
              <span className="result-count">{crateFinderResults.youtube.length} results</span>
              {crateFinderResults.youtube.length > 0 && (
                <span className="playback-badge">▶ Instant Play</span>
              )}
            </div>
            {crateFinderResults.youtube.length > 0 ? (
              <div className="crate-finder-grid">
                {crateFinderResults.youtube.map((result) => (
                  <div 
                    key={result.video_id} 
                    className="crate-finder-card youtube-card"
                    onClick={() => playCrateFinderTrack(result, 'youtube')}
                    data-testid={`youtube-result-${result.video_id}`}
                  >
                    <div className="cf-card-thumb">
                      <img 
                        src={`https://img.youtube.com/vi/${result.video_id}/mqdefault.jpg`} 
                        alt={result.title || crateFinderQuery} 
                        onError={handleImageError} 
                      />
                      <div className="play-overlay">▶</div>
                    </div>
                    <div className="cf-card-info">
                      <span className="cf-card-title">{result.title || crateFinderQuery}</span>
                      <span className="cf-card-artist">{result.artist || 'YouTube'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-results">No YouTube results found</p>
            )}
          </div>
        </div>
      ) : (
        <div className="crate-finder-empty">
          <div className="empty-icon">🔍</div>
          <h4>Find Any Track</h4>
          <p>Search for an artist or track name to find it across all platforms</p>
          <div className="platform-logos">
            <span className="platform-tag spotify">🎵 Spotify</span>
            <span className="platform-tag youtube">▶️ YouTube</span>
            <span className="platform-tag discogs">📀 Discogs</span>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="diggin-crates-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>
      {/* Main Modal Container */}
      <div className="diggin-crates-modal-wrapper">
        {/* Sliding Filter Panel - LEFT side (mirrors Chat Box on right) */}
        <div className={`crate-filters-slide-panel ${showFilters ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
          <div className="crate-filters-panel-content">
            <div className="filters-header">
              <span className="filters-title">☰ Filters</span>
              <span className="filters-hint">Choose filters & refresh</span>
              <button className="close-filters" onClick={() => setShowFilters(false)}>×</button>
            </div>
            
            <div className="filter-count">{tracks.length} releases</div>
            
            <div className="filters-content">
              <div className="filter-section">
                <div className="filter-label">
                  <span className="filter-icon">🎵</span>
                  Genre
                </div>
                <select 
                  value={selectedGenre} 
                  onChange={(e) => setSelectedGenre(e.target.value)}
                  className="filter-select"
                >
                  {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              
              <div className="filter-section">
                <div className="filter-label">
                  <span className="filter-icon">📍</span>
                  Country
                </div>
                <select 
                  value={selectedCountry} 
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="filter-select"
                >
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              
              <div className="filter-section">
                <div className="filter-label">
                  <span className="filter-icon">📅</span>
                  Decade
                </div>
                <select 
                  value={selectedDecade} 
                  onChange={(e) => setSelectedDecade(e.target.value)}
                  className="filter-select"
                >
                  {DECADES.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              
              <div className="filter-section">
                <div className="filter-label">
                  <span className="filter-icon">💎</span>
                  Rarity
                </div>
                <div className="rarity-buttons">
                  {RARITIES.map(r => (
                    <button
                      key={r.id}
                      className={`rarity-btn ${selectedRarity === r.id ? 'active' : ''}`}
                      onClick={() => setSelectedRarity(r.id)}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <button className="refresh-tracks-btn" onClick={handleRefreshTracks} disabled={isLoading}>
                {isLoading ? '⏳ Loading...' : '🔄 Refresh Tracks'}
              </button>
              <button className="reset-filters-btn" onClick={resetFilters}>Reset Filters</button>
              {isMobile && (
                <button className="close-filters-btn-mobile" onClick={() => setShowFilters(false)}>
                  ✕ Close
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Modal */}
        <div className={`diggin-crates-modal ${showFilters ? 'filters-open' : ''}`}>
          <div className="diggin-crates-container">
            {/* Close button */}
            {onClose && (
              <button className="diggin-modal-close" onClick={onClose} title="Close">
                ×
              </button>
            )}
            
            {/* Header with tabs */}
            <div className="crates-header">
              <button 
                className={`filter-toggle ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                ☰ Filters
              </button>
              
              <nav className="crates-nav">
                <button 
                  className={`nav-tab ${activeTab === 'the-station' ? 'active' : ''}`}
                  onClick={() => setActiveTab('the-station')}
                >
                  The Station
                </button>
                <button 
                  className={`nav-tab ${activeTab === 'the-dig' ? 'active' : ''}`}
                  onClick={() => setActiveTab('the-dig')}
                >
                  The Dig
                </button>
                <button 
                  className={`nav-tab ${activeTab === 'hot-picks' ? 'active' : ''}`}
                  onClick={() => setActiveTab('hot-picks')}
                >
                  Hot Picks
                </button>
                <button 
                  className={`nav-tab ${activeTab === 'gems' ? 'active' : ''}`}
                  onClick={() => setActiveTab('gems')}
                >
                  Gems
                </button>
                <button 
                  className={`nav-tab ${activeTab === 'crate-finder' ? 'active crate-finder-tab-active' : ''}`}
                  onClick={() => setActiveTab('crate-finder')}
                >
                  🔍 Track Search
                </button>
              </nav>
              
              <div className="powered-by">
                <span className="powered-by-text">powered by</span>
                <a href="https://www.discogs.com" target="_blank" rel="noopener noreferrer" className="discogs-link">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/6/69/Discogs_record_icon.svg" alt="Discogs" className="discogs-logo" />
                  <span className="discogs-name">Discogs</span>
                </a>
              </div>
            </div>
            
            {/* Main content area */}
            <div className="crates-main">
              {/* Content area */}
              <div className={`crates-content ${activeTab === 'the-station' ? 'station-active' : ''} ${activeTab === 'the-dig' || activeTab === 'hot-picks' || activeTab === 'gems' || (activeTab === 'crate-finder' && crateFinderSearched) ? 'dig-active' : ''}`}>
                {console.log('🎵 Crates render - isLoading:', isLoading, 'activeTab:', activeTab)}
                {isLoading && activeTab !== 'crate-finder' ? (
                  <div className="loading-state">
                    <div className="loading-vinyl"></div>
                    <p>Digging through the crates...</p>
                  </div>
                ) : (
                  <>
                    {activeTab === 'the-station' && (
                      <>
                        {console.log('🎵 Rendering The Station tab - calling renderVinylPlayer')}
                        {renderVinylPlayer()}
                      </>
                    )}
              {activeTab === 'hot-picks' && renderHotPicks()}
              {activeTab === 'gems' && renderGems()}
              {activeTab === 'crate-finder' && renderCrateFinder()}
              
              {/* Track list for The Dig - uses memoized digTracks selector (all electronic tracks) */}
              {activeTab === 'the-dig' && (
                <div className="dig-tracks-section">
                  <h3>Browse Releases</h3>
                  <p className="filter-hint">Use filter menu for sort options</p>
                  <div className="dig-tracks-grid-container">
                    {digTracks.map((track) => (
                      <div 
                        key={track.id} 
                        className={`track-card ${currentTrack?.id === track.id ? 'playing' : ''}`}
                        onClick={() => handleTrackSelect(track)}
                      >
                        <div className="crates-track-thumb">
                          {getImageUrl(track) ? (
                            <img src={getImageUrl(track)} alt={track.title} onError={handleImageError} />
                          ) : (
                            <div className="crates-default-track-thumb">🎵</div>
                          )}
                          {/* Quick Play Button */}
                          <button 
                            className="quick-play-btn"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (currentTrack?.id === track.id) {
                                handlePlayPause();
                              } else {
                                await handleSelectAndPlay(track);
                              }
                            }}
                          >
                            {currentTrack?.id === track.id && isPlaying ? '⏸' : '▶'}
                          </button>
                          {track.rarity !== 'common' && (
                            <div className="rarity-indicator" style={{ background: getRarityBadge(track.rarity).bg }}>
                              {getRarityBadge(track.rarity).icon}
                            </div>
                          )}
                        </div>
                        <div className="crates-track-card-info">
                          <span className="crates-card-title">{track.title}</span>
                          <span className="crates-card-artist">{track.artist}</span>
                          {/* Discogs Purchase Link - only show for Discogs-sourced tracks */}
                          {isDiscogsTrack(track) && (
                            <a 
                              href={`https://www.discogs.com/release/${track.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="discogs-buy-link"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onTouchStart={(e) => {
                                e.stopPropagation();
                              }}
                              onTouchEnd={(e) => {
                                e.stopPropagation();
                                window.open(`https://www.discogs.com/release/${track.id}`, '_blank');
                              }}
                            >
                              Buy on Discogs →
                            </a>
                          )}
                        </div>
                        {renderTrackActions(track, true)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Bottom player bar */}
      {renderBottomPlayer()}
          </div>
        </div>
      </div>
      
      {/* Discord Share Modal */}
      <DiscordShareModal
        isOpen={!!discordShareTrack}
        onClose={() => setDiscordShareTrack(null)}
        track={discordShareTrack}
        shareType="track"
      />

      {/* Add to Crate Modal */}
      {showAddToCrate && (
        <div 
          className="add-to-crate-modal-overlay"
          onClick={() => setShowAddToCrate(null)}
        >
          <div 
            className="add-to-crate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="atc-modal-header">
              <h3>Add to Crate</h3>
              <button 
                className="atc-close-btn"
                onClick={() => setShowAddToCrate(null)}
              >
                ×
              </button>
            </div>

            {/* Track Preview */}
            <div className="atc-track-preview">
              <div className="atc-track-artwork">
                {showAddToCrate.thumb || showAddToCrate.artwork ? (
                  <img src={showAddToCrate.thumb || showAddToCrate.artwork} alt="" />
                ) : (
                  <div className="atc-no-artwork">🎵</div>
                )}
              </div>
              <div className="atc-track-info">
                <div className="atc-track-title">{showAddToCrate.title || showAddToCrate.name}</div>
                <div className="atc-track-artist">{showAddToCrate.artist}</div>
              </div>
            </div>

            {/* Crate List */}
            <div className="atc-crate-list">
              {isLoadingCrates ? (
                <div className="atc-loading">Loading your crates...</div>
              ) : userCrates.length === 0 ? (
                <div className="atc-empty">
                  <p>No crates yet</p>
                  <small>Create a crate in the Crate Box tab first</small>
                </div>
              ) : (
                userCrates.map(crate => (
                  <button
                    key={crate.id}
                    className={`atc-crate-item ${addingToCrate === crate.id ? 'adding' : ''}`}
                    onClick={() => handleAddToCrate(crate.id, showAddToCrate)}
                    disabled={addingToCrate !== null}
                  >
                    <div className="atc-crate-icon">
                      {crate.type === 'live' ? '✓' : '📝'}
                    </div>
                    <div className="atc-crate-details">
                      <span className="atc-crate-name">{crate.name}</span>
                      <span className="atc-crate-meta">
                        {crate.track_count || 0} tracks • {crate.type}
                      </span>
                    </div>
                    {addingToCrate === crate.id && (
                      <div className="atc-adding-spinner">⏳</div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigginInTheCrates;
